// @ts-nocheck
/**
 * ダメージ計算モジュール
 * RPGバトルにおけるダメージ計算を担当
 * @module twinKey/core/battle/DamageCalculator
 */

import {
  ATTRIBUTE_ADVANTAGE,
  ATTRIBUTE_RIVALRY,
  ATTRIBUTE_MULTIPLIERS,
  CRITICAL,
  MIN_DAMAGE_REDUCTION,
} from '../../data/constants';

// ============================================================
// メインダメージ計算
// ============================================================

/**
 * ダメージ計算
 * ダメージ = 攻撃力 × スキル倍率 × (1 - 軽減率) × 属性相性 × クリティカル × バフ/デバフ × 装備ボーナス
 * @param {Object} attacker - 攻撃者
 * @param {Object} defender - 防御者
 * @param {number} skillMultiplier - スキル倍率（通常攻撃=1.0）
 * @param {Object} options - オプション
 * @returns {{ damage: number, isCritical: boolean, effectiveness: string, details: Object }}
 */
export function calculateDamage(attacker, defender, skillMultiplier = 1.0, options = {}) {
  const baseAttack = attacker.stats.attack;
  const defense = defender.stats.defense;

  // バフ・デバフによる攻撃力修正
  const atkBuffMod = getBuffModifier(attacker, 'atk_up');
  const atkDebuffMod = getDebuffModifier(attacker, 'atk_down');
  const effectiveAttack = baseAttack * (1 + atkBuffMod) * (1 - atkDebuffMod);

  // バフ・デバフによる防御力修正
  const defBuffMod = getBuffModifier(defender, 'def_up');
  const defDebuffMod = getDebuffModifier(defender, 'def_down');
  const effectiveDefense = defense * (1 + defBuffMod) * (1 - defDebuffMod);

  // 軽減率 = max(15%, 防御×1.5 / (100 + 防御×1.5))
  const damageReduction = calculateDamageReduction(effectiveDefense);

  // 防御中は軽減率1.5倍（最大50%）
  let effectiveReduction = defender.isDefending
    ? Math.min(0.5, damageReduction * 1.5)
    : damageReduction;

  // 被ダメージ軽減バフ
  const damageReduceBuff = getBuffModifier(defender, 'damage_reduce');
  effectiveReduction = Math.min(0.8, effectiveReduction + damageReduceBuff);

  // 装備による被ダメージ軽減（defender側）
  const equipDamageReduce = defender.equipmentMods?.damageReduce || 0;
  effectiveReduction = Math.min(0.8, effectiveReduction + equipDamageReduce);

  // 無敵チェック
  if (hasActiveBuff(defender, 'invincible')) {
    // 無敵バフを消費
    consumeBuff(defender, 'invincible');
    return {
      damage: 0,
      isCritical: false,
      effectiveness: 'blocked',
      details: { blocked: true, reason: '無敵' },
    };
  }

  // 回避判定（evasion）
  const evasionChance = defender.equipmentMods?.evasion || 0;
  if (evasionChance > 0 && Math.random() < evasionChance) {
    return {
      damage: 0,
      isCritical: false,
      effectiveness: 'evaded',
      details: { evaded: true, evasionChance },
    };
  }

  // 属性相性
  const { multiplier, effectiveness } = getAttributeMultiplier(
    attacker.attribute,
    defender.attribute
  );

  // 装備による属性ダメージボーナス（自分が指定属性の場合、与ダメージ+X%）
  let attrDamageBoost = 1.0;
  if (attacker.equipmentMods?.attributeBonuses) {
    // 自分の属性に対するボーナスを取得
    const selfAttrBonus = attacker.equipmentMods.attributeBonuses[attacker.attribute] || 0;
    attrDamageBoost = 1.0 + selfAttrBonus;
  }

  // 装備による属性特効（attribute_killer: 指定属性への攻撃時、与ダメージ+X%）
  let attrKillerBonus = 1.0;
  if (attacker.equipmentMods?.attributeKillers) {
    const killerValue = attacker.equipmentMods.attributeKillers[defender.attribute] || 0;
    attrKillerBonus = 1.0 + killerValue;
  }

  // 装備による属性耐性（attribute_resistance: 指定属性からの被ダメージ-X%）
  let attrResistMod = 1.0;
  if (defender.equipmentMods?.attributeResists) {
    const resistValue = defender.equipmentMods.attributeResists[attacker.attribute] || 0;
    attrResistMod = 1.0 - resistValue;
  }

  const effectiveAttrMultiplier = multiplier * attrDamageBoost * attrKillerBonus * attrResistMod;

  // クリティカル判定（バフ + 装備によるクリティカル率上昇）
  const critBuffMod = getBuffModifier(attacker, 'crit_up');
  const equipCritRate = attacker.equipmentMods?.critRate || 0;
  const critRate = Math.min(0.5, CRITICAL.rate + critBuffMod + equipCritRate); // 最大50%
  const critRoll = options.forceCrit !== undefined ? options.forceCrit : Math.random();
  const isCritical = critRoll < critRate;

  // クリティカル倍率（装備によるクリティカルダメージ上昇）
  const equipCritDmg = attacker.equipmentMods?.critDmg || 0;
  const critMultiplier = isCritical ? (CRITICAL.multiplier + equipCritDmg) : 1.0;

  // 装備による与ダメージボーナス（attacker側）
  const equipDamageBonus = 1.0 + (attacker.equipmentMods?.damageBonus || 0);

  // ダメージ計算
  let damage = Math.floor(
    effectiveAttack * skillMultiplier * (1 - effectiveReduction) * effectiveAttrMultiplier * critMultiplier * equipDamageBonus
  );

  // 最低ダメージ保証（1ダメージ）
  damage = Math.max(1, damage);

  return {
    damage,
    isCritical,
    effectiveness,
    lifesteal: attacker.equipmentMods?.lifesteal || 0, // 吸収率を結果に含める
    details: {
      baseAttack,
      effectiveAttack,
      defense,
      effectiveDefense,
      skillMultiplier,
      damageReduction: effectiveReduction,
      attributeMultiplier: effectiveAttrMultiplier,
      critMultiplier,
      isDefending: defender.isDefending,
      atkBuffMod,
      atkDebuffMod,
      defBuffMod,
      defDebuffMod,
      equipDamageBonus,
      equipDamageReduce,
      equipCritRate,
    },
  };
}

// ============================================================
// バフ・デバフ計算ヘルパー
// ============================================================

/**
 * バフによる修正値を取得
 * @param {Object} unit - ユニット
 * @param {string} buffType - バフタイプ
 * @returns {number} 修正値（0.0〜）
 */
export function getBuffModifier(unit, buffType) {
  if (!unit.buffs) return 0;
  const buff = unit.buffs.find(b => b.type === buffType);
  return buff ? buff.value : 0;
}

/**
 * デバフによる修正値を取得
 * @param {Object} unit - ユニット
 * @param {string} debuffType - デバフタイプ
 * @returns {number} 修正値（0.0〜）
 */
export function getDebuffModifier(unit, debuffType) {
  if (!unit.debuffs) return 0;
  const debuff = unit.debuffs.find(d => d.type === debuffType);
  return debuff ? debuff.value : 0;
}

/**
 * 特定のバフがアクティブかチェック
 * @param {Object} unit - ユニット
 * @param {string} buffType - バフタイプ
 * @returns {boolean}
 */
export function hasActiveBuff(unit, buffType) {
  if (!unit.buffs) return false;
  return unit.buffs.some(b => b.type === buffType && (b.stacks > 0 || b.duration > 0));
}

/**
 * バフを1スタック消費
 * @param {Object} unit - ユニット
 * @param {string} buffType - バフタイプ
 */
export function consumeBuff(unit, buffType) {
  if (!unit.buffs) return;
  const buff = unit.buffs.find(b => b.type === buffType);
  if (buff) {
    if (buff.stacks && buff.stacks > 0) {
      buff.stacks--;
      if (buff.stacks <= 0) {
        unit.buffs = unit.buffs.filter(b => b.type !== buffType);
      }
    } else {
      unit.buffs = unit.buffs.filter(b => b.type !== buffType);
    }
  }
}

/**
 * 軽減率を計算
 * @param {number} defense - 防御力
 * @returns {number} 軽減率（0.15 ~ ）
 */
export function calculateDamageReduction(defense) {
  // 軽減率 = max(15%, 防御×1.5 / (100 + 防御×1.5))
  const scaledDefense = defense * 1.5;
  const reduction = scaledDefense / (100 + scaledDefense);
  return Math.max(MIN_DAMAGE_REDUCTION, reduction);
}

// ============================================================
// 属性相性
// ============================================================

/**
 * 属性相性倍率を取得
 * @param {string} attackerAttr - 攻撃者の属性
 * @param {string} defenderAttr - 防御者の属性
 * @returns {{ multiplier: number, effectiveness: 'effective' | 'not_effective' | 'rivalry' | 'neutral' }}
 */
export function getAttributeMultiplier(attackerAttr, defenderAttr) {
  // 三すくみ有利チェック
  const advantage = ATTRIBUTE_ADVANTAGE[attackerAttr];
  if (advantage === defenderAttr) {
    return {
      multiplier: ATTRIBUTE_MULTIPLIERS.effective,
      effectiveness: 'effective',
    };
  }

  // 三すくみ不利チェック（逆引き）
  for (const [attr, adv] of Object.entries(ATTRIBUTE_ADVANTAGE)) {
    if (adv === attackerAttr && attr === defenderAttr) {
      return {
        multiplier: ATTRIBUTE_MULTIPLIERS.not_effective,
        effectiveness: 'not_effective',
      };
    }
  }

  // 対立軸チェック（原始 ↔ 未来）
  const rivalry = ATTRIBUTE_RIVALRY[attackerAttr];
  if (rivalry === defenderAttr) {
    return {
      multiplier: ATTRIBUTE_MULTIPLIERS.rivalry,
      effectiveness: 'rivalry',
    };
  }

  // 中立
  return {
    multiplier: ATTRIBUTE_MULTIPLIERS.neutral,
    effectiveness: 'neutral',
  };
}

/**
 * 属性相性のテキスト表示を取得
 * @param {string} effectiveness - 相性タイプ
 * @returns {string} 表示テキスト
 */
export function getEffectivenessText(effectiveness) {
  switch (effectiveness) {
    case 'effective':
      return '効果抜群！';
    case 'rivalry':
      return '時空の対立！';
    case 'not_effective':
      return 'いまひとつ...';
    default:
      return '';
  }
}

// ============================================================
// 回復計算
// ============================================================

/**
 * 回復量を計算
 * @param {Object} target - 回復対象
 * @param {number} healPercent - 回復割合（0-1）
 * @param {number} fixedAmount - 固定回復量
 * @returns {{ heal: number, newHp: number, overHeal: number }}
 */
export function calculateHeal(target, healPercent = 0, fixedAmount = 0) {
  const currentHp = target.stats.currentHp;
  const maxHp = target.stats.maxHp;

  // 回復量 = 固定値 + 最大HP × 割合
  const healAmount = Math.floor(fixedAmount + maxHp * healPercent);

  // 最大HPを超えない
  const newHp = Math.min(maxHp, currentHp + healAmount);
  const actualHeal = newHp - currentHp;
  const overHeal = healAmount - actualHeal;

  return {
    heal: actualHeal,
    newHp,
    overHeal,
  };
}

// ============================================================
// ダメージプレビュー
// ============================================================

/**
 * 予想ダメージを計算（UIプレビュー用）
 * クリティカルなし、平均値として表示
 * @param {Object} attacker - 攻撃者
 * @param {Object} defender - 防御者
 * @param {number} skillMultiplier - スキル倍率
 * @returns {{ min: number, max: number, average: number, effectiveness: string }}
 */
export function calculateDamagePreview(attacker, defender, skillMultiplier = 1.0) {
  // 通常ダメージ（クリティカルなし）
  const normal = calculateDamage(attacker, defender, skillMultiplier, { forceCrit: 1 });

  // クリティカルダメージ
  const crit = calculateDamage(attacker, defender, skillMultiplier, { forceCrit: 0 });

  // 平均 = 通常 × 90% + クリティカル × 10%
  const average = Math.floor(normal.damage * 0.9 + crit.damage * 0.1);

  return {
    min: normal.damage,
    max: crit.damage,
    average,
    effectiveness: normal.effectiveness,
  };
}

/**
 * ダメージ計算式の詳細テキストを取得（デバッグ用）
 * @param {Object} details - calculateDamageの詳細結果
 * @returns {string} 計算式テキスト
 */
export function getDamageFormulaText(details) {
  const {
    baseAttack,
    skillMultiplier,
    damageReduction,
    attributeMultiplier,
    critMultiplier,
  } = details;

  return `${baseAttack} × ${skillMultiplier.toFixed(2)} × (1 - ${(damageReduction * 100).toFixed(1)}%) × ${attributeMultiplier.toFixed(2)} × ${critMultiplier.toFixed(1)}`;
}

// ============================================================
// AOEダメージ
// ============================================================

/**
 * 全体攻撃のダメージを計算
 * @param {Object} attacker - 攻撃者
 * @param {Array} defenders - 防御者配列
 * @param {number} skillMultiplier - スキル倍率
 * @returns {Array} 各ターゲットへのダメージ結果配列
 */
export function calculateAoeDamage(attacker, defenders, skillMultiplier = 1.0) {
  return defenders
    .filter(d => d.isAlive)
    .map(defender => ({
      targetId: defender.id,
      targetName: defender.name,
      ...calculateDamage(attacker, defender, skillMultiplier),
    }));
}
